import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { DataToolbar } from "@/components/layout/DataTable";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

export default function AuditLogPage() {
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;
  const [search, setSearch] = useState("");

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit-logs", branchId],
    queryFn: async () => {
      const { data } = await api.get("/audit-logs", { params: { branch_id: branchId, limit: 200 } });
      return data.audit_logs;
    },
    enabled: Boolean(branchId),
  });

  const filtered = logs.filter(
    (l) =>
      l.module.toLowerCase().includes(search.toLowerCase()) ||
      l.action.toLowerCase().includes(search.toLowerCase()) ||
      (l.user || "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <PageContainer>
      <PageHeader
        title="Audit Log"
        subtitle={`Immutable trail of changes — ${user?.default_branch?.name || "branch"}`}
      />
      <DataToolbar searchValue={search} onSearch={setSearch} testIdPrefix="audit" />
      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="h-40 grid place-items-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="overflow-auto max-h-[640px]">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50 text-slate-500 border-b sticky top-0">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">When</th>
                  <th className="text-left font-medium px-4 py-2.5">User</th>
                  <th className="text-left font-medium px-4 py-2.5">Module</th>
                  <th className="text-left font-medium px-4 py-2.5">Action</th>
                  <th className="text-left font-medium px-4 py-2.5">Record</th>
                  <th className="text-left font-medium px-4 py-2.5">IP</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => (
                  <tr key={log.id} className="border-t hover:bg-slate-50/60" data-testid={`audit-row-${log.id}`}>
                    <td className="px-4 py-2 font-mono text-[11.5px] text-slate-600 whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2">{log.user}</td>
                    <td className="px-4 py-2">
                      <Pill tone="blue">{log.module}</Pill>
                    </td>
                    <td className="px-4 py-2 capitalize">{log.action}</td>
                    <td className="px-4 py-2 font-mono text-[11.5px] text-slate-600">
                      {log.record_type}{log.record_id ? ` #${log.record_id}` : ""}
                    </td>
                    <td className="px-4 py-2 font-mono text-[11px] text-slate-500">{log.ip_address || "—"}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      No audit entries yet. Changes to API data will appear here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
}
